import type { HomeAppointment } from "@gioia365/contracts";
import { Link } from "@tanstack/react-router";
import { useId } from "react";
import { StatusPill } from "../design/components/StatusPill";
import { formatCount, formatNumericTime } from "../i18n/format";
import { useLocale, useT, type Translate } from "../i18n/locale";
import "./home.css";

// Le disco e le demo di oggi nella Home «Oggi» (SEZ-5; mock-up `home.html`, scheda «Disco prenotate»): la scheda con quante disco ci
// sono oggi e la prossima da aprire, e sotto l'elenco di disco e demo di oggi, in ordine di ora, con lo stato che dice il server
// (da fare, in corso, da chiudere, fatta, il Lead non si è presentato), il link Meet quando Google l'ha creato e il Lead da aprire.
// Gli esiti si segnano dalla scheda del Lead (TASK-11): qui si legge soltanto. Ore nel fuso dell'azienda (UX-5).

type Props = { appointments: readonly HomeAppointment[]; timeZone: string };

const leadName = (appointment: HomeAppointment) => appointment.lead.displayName ?? appointment.lead.salonName ?? "—";

/** La prossima disco da fare: la prima, in ordine di ora, non ancora finita. */
function nextDisco(appointments: readonly HomeAppointment[]): HomeAppointment | undefined {
  return appointments.find((appointment) => appointment.kind === "disco" && (appointment.state === "to_do" || appointment.state === "in_progress"));
}

/** La scheda «Disco prenotate» del blocco «Oggi». */
export function DiscoTodayCard({ appointments, timeZone }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const labelId = useId();
  const discos = appointments.filter((appointment) => appointment.kind === "disco").length;
  const next = nextDisco(appointments);
  return (
    <article className="g-card g-home-today__card" aria-labelledby={labelId}>
      <p id={labelId} className="g-home-today__label">
        {t("homeToday.disco")}
      </p>
      <p className="g-home-today__value">{t("homeToday.discoValue", { count: formatCount(locale, discos) })}</p>
      <p className="g-home-today__detail">
        {next ? t("homeToday.discoNext", { lead: leadName(next), time: formatNumericTime(timeZone, next.startsAt) }) : t("homeToday.discoNone")}
      </p>
      {next ? (
        <Link to="/lead/$leadId" params={{ leadId: next.lead.id }} className="g-button g-home-today__action">
          {t("homeToday.openNextDisco")}
        </Link>
      ) : null}
    </article>
  );
}

function stateText(t: Translate, appointment: HomeAppointment): string {
  return t(`homeToday.appointmentState.${appointment.state}`);
}

const TONE: Record<HomeAppointment["state"], "neutral" | "success" | "warning"> = {
  to_do: "neutral",
  in_progress: "warning",
  to_close: "warning",
  held: "success",
  no_show: "neutral",
};

const ICON: Record<HomeAppointment["state"], "clock" | "check" | "alert" | "minus"> = {
  to_do: "clock",
  in_progress: "clock",
  to_close: "alert",
  held: "check",
  no_show: "minus",
};

/** «Disco e demo di oggi»: una riga per appuntamento, il Lead apre la sua scheda. */
export function AppointmentsTodayList({ appointments, timeZone }: Props) {
  const t = useT();
  const titleId = useId();
  return (
    <section className="g-home-appointments" aria-labelledby={titleId}>
      <h3 id={titleId} className="g-home-appointments__title">
        {t("homeToday.appointments")}
      </h3>
      {appointments.length === 0 ? (
        <p className="g-home-today__detail">{t("homeToday.appointmentsNone")}</p>
      ) : (
        <ul className="g-home-appointments__list">
          {appointments.map((appointment) => (
            <li key={appointment.id} className="g-home-appointments__item" data-state={appointment.state}>
              <Link to="/lead/$leadId" params={{ leadId: appointment.lead.id }} className="g-home-appointments__lead">
                {t("homeToday.appointmentItem", {
                  time: formatNumericTime(timeZone, appointment.startsAt),
                  kind: t(`appointments.kind.${appointment.kind}`),
                  lead: leadName(appointment),
                })}
              </Link>
              <span className="g-home-appointments__facts">
                {appointment.lead.displayName !== null && appointment.lead.salonName ? <span>{appointment.lead.salonName}</span> : null}
                <StatusPill tone={TONE[appointment.state]} icon={ICON[appointment.state]}>
                  {stateText(t, appointment)}
                </StatusPill>
                {appointment.meetUrl ? (
                  <a href={appointment.meetUrl} target="_blank" rel="noopener noreferrer">
                    {t("appointments.block.meet")}
                  </a>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
